"use client";

import { motion, type Variants } from "framer-motion";
import Image, { type StaticImageData } from "next/image";
import { BackLink } from "@/components/back-link";

import revenueDashboard from "@/app/assets/craft/revenue-dashboard.png";
import onboarding from "@/app/assets/craft/onboarding.png";
import coachProfile from "@/app/assets/craft/coach-profile.png";
import talentPool from "@/app/assets/craft/talent-pool.png";
import candidateAnalysis from "@/app/assets/craft/seed-6.png";
import candidateScoring from "@/app/assets/craft/seed-7.png";

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { delayChildren: 0, staggerChildren: 0.15 },
  },
};

const sectionVariants: Variants = {
  hidden: { opacity: 0, scale: 0.99, y: 5, filter: "blur(2px)" },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, scale: 0.99, y: 10, filter: "blur(2px)" },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

interface CraftItem {
  src: StaticImageData;
  title: string;
  date: string;
}

const items: CraftItem[] = [
  {
    src: candidateScoring,
    title: "AI candidate scoring",
    date: "2026",
  },
  {
    src: revenueDashboard,
    title: "Revenue dashboard",
    date: "2026",
  },
  {
    src: candidateAnalysis,
    title: "Candidate analysis",
    date: "2026",
  },
  {
    src: onboarding,
    title: "Coach onboarding wizard",
    date: "2026",
  },
  {
    src: coachProfile,
    title: "Coach profile card",
    date: "2026",
  },
  {
    src: talentPool,
    title: "Talent Pool",
    date: "2026",
  },
];

export default function CraftPage() {
  return (
    <div className="mx-auto w-full max-w-4xl">
      <motion.div
        className="mt-16 flex flex-col gap-8 select-none"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div
          className="flex flex-col gap-4 max-w-xl mx-auto w-full"
          variants={sectionVariants}
        >
          <BackLink href="/" label="[Index]" className="mb-2" />
          <h1 className="text-3xl tracking-tight">Craft</h1>
          <p className="font-medium text-[var(--sand-10)]">
            A glimpse into the{" "}
            <span className="text-[var(--sand-12)] font-semibold">
              products
            </span>{" "}
            I help build. Designed with{" "}
            <span className="text-[var(--sand-12)] font-semibold">care</span>,
            shipped with{" "}
            <span className="text-[var(--sand-12)] font-semibold">impact</span>.
          </p>
          <div className="h-px w-full bg-[var(--sand-6)] mt-4" />
        </motion.div>

        <motion.div
          className="columns-1 sm:columns-2 lg:columns-3 gap-4"
          variants={sectionVariants}
        >
          {items.map((item) => (
            <motion.div
              key={item.title}
              variants={itemVariants}
              className="break-inside-avoid mb-4 border border-[var(--sand-4)] rounded-xl shadow-sm overflow-hidden"
            >
              <div className="flex items-center justify-between px-4 pt-2 pb-4 text-sm font-medium bg-[var(--sand-2)] -mb-2">
                <span className="text-[var(--sand-11)]">{item.title}</span>
                <span className="text-[var(--sand-9)]">{item.date}</span>
              </div>
              <div className="relative rounded-xl overflow-hidden border-t border-[var(--sand-4)] bg-[var(--sand-12)]">
                <Image
                  src={item.src}
                  alt={item.title}
                  className="w-full h-auto object-cover"
                  placeholder="blur"
                />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
}
